import React, { useState, ReactNode } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/ui-components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { toast } from "@/lib/toast";
import { useMediaLibraryDelete, useMediaLibraryGetUsage } from "@/lib/api/requests/media-library.requests";
import { MediaLibraryItemDto } from '@/lib/api/interfaces/media-library.interface';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";

interface DeleteMediaDialogProps {
  /**
   * The trigger element that opens the dialog (optional)
   */
  trigger?: ReactNode;
  /**
   * Whether the dialog is open
   */
  open?: boolean;
  /**
   * Callback when dialog open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * The media item to delete
   */
  mediaItem: MediaLibraryItemDto;
  /**
   * Callback function called after successful deletion
   */
  onSuccess?: () => void;
}

/**
 * Dialog component for deleting media items
 */
export const DeleteMediaDialog: React.FC<DeleteMediaDialogProps> = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  mediaItem,
  onSuccess,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const { isInternal } = usePermissions();

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const usageQuery = useMediaLibraryGetUsage(mediaItem.mediaId, { enabled: open });

  // API hook for deletion
  const deleteMutation = useMediaLibraryDelete({
    onSuccess: () => {
      toast.success('Mediendatei erfolgreich gelöscht!', {
        description: `"${mediaItem.displayName}" wurde entfernt.`,
        duration: 3000,
      });
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Failed to delete media item:', error);
      toast.error('Fehler beim Löschen', {
        description: 'Die Mediendatei konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
        duration: 5000,
        error,
      });
    },
  });

  const handleDelete = async () => {
      await deleteMutation.mutateAsync(mediaItem.mediaId);
  };

  const isLoading = deleteMutation.isPending;
  const usageLoading = usageQuery.isLoading;
  const usageError = usageQuery.isError;
  const customerTemplatesCount = usageQuery.data?.customerTemplatesCount ?? 0;
  const storeTemplatesCount = usageQuery.data?.storeTemplatesCount ?? 0;

  return (
    <StandardDeleteDialog
      trigger={trigger}
      open={open}
      onOpenChange={setOpen}
      title="Mediendatei löschen"
      description="Diese Aktion kann nicht rückgängig gemacht werden"
      itemTitle={mediaItem.displayName}
      itemTypeLabel="Typ"
      itemTypeValue={mediaItem.mimeType}
      onDelete={handleDelete}
      isLoading={isLoading}
    >
      <Alert className="border-amber-200 bg-amber-50 text-amber-900 [&>svg]:text-amber-600 mt-2">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle className="mb-2">Wird in Templates verwendet</AlertTitle>
        <AlertDescription className="text-amber-800">
        {usageLoading ? (
          <span>Prüfe Verwendungen…</span>
        ) : usageError ? (
          <span>Verwendungen konnten nicht geprüft werden.</span>
        ) : isInternal ? (
          <span>
            Diese Mediendatei wird in <strong>{customerTemplatesCount}</strong> Kunden-Templates und <strong>{storeTemplatesCount}</strong> Store-Templates verwendet.
          </span>
        ) : (
          <span>Diese Mediendatei wird in <strong>{customerTemplatesCount}</strong> Templates verwendet.</span>
        )}
        </AlertDescription>
      </Alert>
    </StandardDeleteDialog>
  );
};
